import { Box, Toolbar, AppBar, useTheme, TextField, InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Outlet } from 'react-router-dom';
import { useState } from 'react';
import Sidebar from './Sidebar';
import BreadcrumbsBar from './BreadcrumbsBar';
import HeaderRight from '@shared/HeaderRight';
import ForcePasswordChangeDialog from '@shared/ForcePasswordChangeDialog';
import ExportDataDialog from '@components/client/ExportDataDialog';
import { useAuth } from '@contexts/AuthContext';

const DRAWER_WIDTH = 260;

export default function AdminLayout() {
  const { signOut, user, toggleTheme, hasTemporaryPassword } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Get user initials for avatar
  const userInitials = user?.name 
    ? user.name.charAt(0).toUpperCase() 
    : user?.email?.charAt(0).toUpperCase() ?? 'U';

  // Handler pentru export
  const handleExport = () => {
    console.log('🔄 AdminLayout handleExport called!', { 
      userRole: user?.role, 
      userId: user?.id 
    });
    setExportDialogOpen(true);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar onSignOut={signOut} userInitials={userInitials} onToggleTheme={toggleTheme} />

      {/* AppBar */}
      <AppBar 
        position="fixed" 
        color="inherit" 
        elevation={0}
        sx={{ 
          borderBottom: '1px solid',
          borderBottomColor: isDark ? 'divider' : '#eee',
          ...(isDark && {
            boxShadow: '0 1px 8px rgba(0,0,0,0.6)'
          }),
          ml: `${DRAWER_WIDTH}px`, 
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          bgcolor: 'background.paper'
        }}
      >
        <Toolbar sx={{ 
          gap: 2, 
          minHeight: '64px !important',
          px: 2,
          justifyContent: 'space-between'
        }}>
          <BreadcrumbsBar />
          
          {/* Search Bar */}
          <Box sx={{ flex: 1, maxWidth: 400, mx: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Caută după numele animalului, medic sau clinică..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { boxShadow: '0 2px 8px rgba(90,128,200,0.10)' },
                  '&.Mui-focused': { boxShadow: '0 4px 12px rgba(90,128,200,0.20)', borderColor: '#5A80C8' }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          
          {/* Actions: Refresh, Export, Help, Notifications, Avatar */}
          <HeaderRight 
            showExport={true}
            onExport={handleExport}
          />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          bgcolor: 'background.default', 
          minHeight: '100vh',
          p: 2,
          mt: 8,
          maxWidth: { lg: '1400px', xl: '1600px' },
          mx: 'auto',
          width: '100%',
          ml: `${DRAWER_WIDTH}px` // Account for Sidebar width
        }}
      >
        <Outlet />
      </Box>
      
      {/* Force Password Change Dialog */}
      <ForcePasswordChangeDialog 
        open={hasTemporaryPassword} 
        onClose={() => {}} // Nu se poate inchide pana nu schimba parola
      />
      
      {/* Export Data Dialog - GDPR */}
      {user?.role === 'client' && (
        <ExportDataDialog 
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
        />
      )}
    </Box>
  );
}
